/** Flyer tools: fetch a pasted link's images, read a flyer with Gemini, store the chosen one. */
import { send, sendForm } from "./request";
import type { Draft } from "./types";

export interface Slide {
  data_url: string;
  width: number | null;
  height: number | null;
}
export interface Fetched {
  slides: Slide[];
  caption: string;
}
/** An image a sheet holds until it saves; nothing is stored before then. */
export interface Picked {
  blob: Blob;
  preview: string;
  caption: string;
}
export interface Reading {
  draft: {
    date: string | null;
    venue_name: string;
    venue_location: string | null;
    venue_known: boolean;
    event_name: string | null;
    bands: string[];
  };
  notes: string | null;
  /** Only Add an upcoming show uses these. */
  listing: { price: string | null; ticket_url: string | null };
}

export const MAX_BYTES = 20 * 1024 * 1024;
export const PHOTO_TYPES = "image/jpeg,image/png,image/webp,image/gif";

export const fetchLink = (url: string) => send<Fetched>("/api/flyers/fetch", "POST", { url: url.trim() }, "Could not load that link");

export function readFlyer(p: Picked): Promise<Reading> {
  const f = new FormData();
  f.append("file", p.blob, "flyer");
  f.append("caption", p.caption);
  return sendForm<Reading>("/api/flyers/read", f, "Could not read the flyer");
}

export async function storeFlyer(p: Picked, headliner: string, date: string, folder: "attended" | "upcoming" = "attended"): Promise<string> {
  const f = new FormData();
  f.append("file", p.blob, "flyer");
  f.append("headliner", headliner);
  f.append("date", date);
  f.append("folder", folder);
  return (await sendForm<{ url: string }>("/api/flyers", f, "Could not save the flyer")).url;
}

export const setShowFlyer = (id: number, image_url: string | null) =>
  send(`/api/shows/${id}/image`, "PUT", { image_url }, "Could not save the flyer");

/** Google Images search for this show's flyer. */
export function searchUrl(q: { event: string; bands: string[]; venue: string; date: string }): string {
  const who = q.event.trim() ? `${q.event.trim()} ${q.bands[0] ?? ""}` : q.bands.join(", ");
  const query = [who, q.venue, q.date].map((s) => s.trim()).filter(Boolean).join(" ");
  return `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`;
}

/** Why a pasted link cannot be used yet, or null. */
export function linkProblem(text: string): string | null {
  const t = text.trim();
  if (!t) return "Paste a link first.";
  if (t.startsWith("data:")) return "That is a thumbnail from the search results. Open the image full size, then copy its address.";
  if (!/^https?:\/\//i.test(t)) return "Paste a link that starts with https://";
  return null;
}

export function blobFromDataUrl(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  const type = /^data:([^;,]+)/.exec(dataUrl)?.[1] ?? "image/jpeg";
  const bin = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

export const pickedFromSlide = (s: Slide, caption: string): Picked => ({ blob: blobFromDataUrl(s.data_url), preview: s.data_url, caption });

export function pickedFromFile(file: File): Promise<Picked> {
  if (file.size > MAX_BYTES) return Promise.reject(new Error("That photo is over 20 MB."));
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ blob: file, preview: String(r.result), caption: "" });
    r.onerror = () => reject(new Error("Could not open that photo."));
    r.readAsDataURL(file);
  });
}

/** A flyer already in our bucket is saved as-is; any other URL is copied first. */
export const isStored = (url: string | null) => Boolean(url && url.startsWith("https://storage.googleapis.com/"));

/** The draft with what the flyer said filled in; what it did not say keeps the draft's value. */
export function applyReading(d: Draft, r: Reading["draft"]): Draft {
  const venue = r.venue_name.trim();
  return {
    ...d,
    date: r.date ?? d.date,
    bands: r.bands.length ? r.bands : d.bands,
    venue_name: venue || d.venue_name,
    venue_location: venue ? (r.venue_location ?? "") : d.venue_location,
    event_name: r.event_name ?? d.event_name,
  };
}
